import { isFRM, isKYCApp, isMVV, isV2BankFlow } from './brandHelpers';
import { injectedStore as store } from '../globals/StoreAccess';
import { isWeb } from './isWeb';
import { UserDataOutput } from '../@types';

const isDashboardScreen = () => {
  return (
    isWeb() &&
    (document.location.href.includes('/dashboard') ||
      document.location.href.includes('/settings'))
  );
};

// Treat these payment statuses as "active" for onboarding completion logic
const ACTIVE_PAYMENT_STATUSES = ['verified', 'pending'];
const hasActivePaymentMethod = (user: any): boolean =>
  !!user?.paymentMethods?.length &&
  !!user?.paymentMethods?.find((payment: any) =>
    ACTIVE_PAYMENT_STATUSES.includes(payment?.status)
  );

//
// ──────────────────────────────────────────────────────────────────────────────
//   > Shared Constants
// ──────────────────────────────────────────────────────────────────────────────
//

// Define custom client-side steps to be added to the onboarding flow
const CUSTOM_STEPS = {
  DOB: {
    screen: '/user/dob',
    routeSuffix: 'dob',
    shouldShow: () => true,
    isCompleted: (user) => {
      return !!user?.dateOfBirth;
    },
  },
  SCHUFA: {
    screen: '/user/schufa',
    routeSuffix: 'schufa',
    // We don't show the schufa step if payment methods isn't required like in Child tariff partners
    shouldShow: (user) =>
      user?.challenges
        ?.find((challenge: any) => challenge?.type === 'firstSteps')
        ?.tasks?.find((task) => task?.screen === '/user/payment-methods'),
    isCompleted: (user) => {
      return !user?.schufaCheckRequired;
    },
  },
  // Add more custom steps as needed here
};

// TODO: Order should depend on ordered user challenges from api side
const KYC_ONBOARDING_ORDER = [
  '/user/verify-email',
  '/user/verify-phone-number',
  '/user/dob',
  '/user/select-tariff',
  '/user/verify-id-document',
  '/user/verify-drivers-license',
  '/user/payment-methods',
  '/user/enter-pin',
  '/user/pay-deposit',
];

const KYC_ONBOARDING_ORDER_WITH_SCHUFA_SPLITTED = [
  '/user/verify-email',
  '/user/verify-phone-number',
  '/user/dob',
  '/user/select-tariff',
  '/user/schufa',
  '/user/verify-id-document',
  '/user/verify-drivers-license',
  '/user/payment-methods',
  '/user/enter-pin',
  '/user/pay-deposit',
];

const KYC_ONBOARDING_ORDER_WITH_SCHUFA_SPLITTED_HIGH_DEPOSIT = [
  '/user/verify-email',
  '/user/verify-phone-number',
  '/user/dob',
  '/user/select-tariff',
  '/user/schufa',
  '/user/payment-methods',
  '/user/pay-deposit',
  '/user/verify-id-document',
  '/user/verify-drivers-license',
  '/user/enter-pin',
];

const KYC_ONBOARDING_ORDER_WITH_TRUELAYER = [
  '/user/verify-email',
  '/user/verify-phone-number',
  '/user/dob',
  '/user/select-tariff',
  '/user/schufa',
  '/user/verify-id-document',
  '/user/verify-drivers-license',
  '/user/payment-methods',
  '/user/enter-pin',
  '/user/pay-deposit',
];

const KYC_ONBOARDING_ORDER_WITH_TRUELAYER_HIGH_DEPOSIT = [
  '/user/verify-email',
  '/user/verify-phone-number',
  '/user/dob',
  '/user/select-tariff',
  '/user/schufa',
  '/user/payment-methods',
  '/user/pay-deposit',
  '/user/verify-id-document',
  '/user/verify-drivers-license',
  '/user/enter-pin',
];

const CAR_SHARING_ONBOARDING_ORDER = [
  '/user/verify-email',
  '/user/verify-phone-number',
  '/user/enter-address',
  '/user/payment-methods',
  '/user/verify-drivers-license',
  '/user/verify-id-document',
];

/**
 * A map from the user’s task screen path (e.g., '/user/verify-email')
 * to the actual sub-route (e.g., 'email') used in your /onboarding or /dashboard stacks.
 */
const SCREEN_TO_ROUTE_SUFFIX: Record<string, string> = {
  '/user/verify-email': 'email',
  '/user/verify-phone-number': 'addPhone',
  '/user/enter-address': 'addAddress',
  '/user/payment-methods': isKYCApp() ? 'optimizedSepa' : 'selectPayment',
  '/user/verify-drivers-license': 'DLOnboarding',
  '/user/verify-id-document': 'IDOnboarding',
  '/user/pay-deposit': 'deposit',
  '/user/select-tariff': 'tariff',
  '/user/enter-pin': 'addPin',
  '/user/dob': 'dob',
  '/user/schufa': 'schufa',
};

const SCREEN_TO_STACK_NAME: Record<string, string> = {
  '/user/verify-email': 'VerifyEmail',
  '/user/verify-phone-number': 'Phone',
  '/user/enter-address': 'Address',
  '/user/payment-methods': 'SelectPayment',
  '/user/verify-drivers-license': 'DL',
  '/user/verify-id-document': 'ID',
};

// Helper function to inject custom client-side steps into the user object
export const injectCustomSteps = (user: UserDataOutput) => {
  if (!user || !user.challenges) return user;

  // Create a deep copy of the user object to avoid modifying the original
  const updatedUser = JSON.parse(JSON.stringify(user));

  // Find the firstSteps challenge
  const firstStepsChallenge = updatedUser.challenges.find(
    (challenge) => challenge.type === 'firstSteps'
  );

  if (!firstStepsChallenge || !firstStepsChallenge.tasks) return updatedUser;

  // Determine which order is currently being used
  const isTruelayerEnabled =
    store?.getState()?.appData?.common?.brandSettings?.trueLayerSettings
      ?.bankLoginEnabled;

  const separateCreditCheckScreen =
    store?.getState()?.appData?.common?.brandSettings?.kycSettings
      ?.separateCreditCheckScreen;

  const isDocumentsAfterDeposit =
    user?.deposit?.toPayForSepaUnlock >
    store?.getState()?.appData?.common?.brandSettings?.kycSettings
      ?.documentsAfterDepositMinAmount;

  const currentOrder = isKYCApp()
    ? separateCreditCheckScreen
      ? isDocumentsAfterDeposit
        ? KYC_ONBOARDING_ORDER_WITH_SCHUFA_SPLITTED_HIGH_DEPOSIT
        : KYC_ONBOARDING_ORDER_WITH_SCHUFA_SPLITTED
      : isTruelayerEnabled
      ? isDocumentsAfterDeposit
        ? KYC_ONBOARDING_ORDER_WITH_TRUELAYER_HIGH_DEPOSIT
        : KYC_ONBOARDING_ORDER_WITH_TRUELAYER
      : KYC_ONBOARDING_ORDER
    : CAR_SHARING_ONBOARDING_ORDER;

  // For each custom step, check if it should be injected for the current order
  Object.values(CUSTOM_STEPS).forEach((customStep) => {
    // Check if the custom step's screen is included in the current order
    const isInCurrentOrder = currentOrder.includes(customStep.screen);

    // Skip injection if the step shouldn't be included in the current order
    if (!isInCurrentOrder) {
      return;
    }

    // Check if the step already exists in the user tasks
    const stepExists = firstStepsChallenge.tasks.some(
      (task) => task.screen === customStep.screen
    );

    // Skip if the step exists or shouldn't be shown based on user data
    if (stepExists || !customStep.shouldShow(updatedUser)) {
      return;
    }

    // Create the custom task object
    const customTask = {
      screen: customStep.screen,
      status: customStep.isCompleted(updatedUser) ? 'completed' : 'open',
      text: `Complete ${customStep.routeSuffix}`,
      isCustomStep: true,
    };
    // Insert the custom task at the determined position
    firstStepsChallenge.tasks.push(customTask);
  });

  return updatedUser;
};

//
// ──────────────────────────────────────────────────────────────────────────────
//   > Shared Helper Functions
// ──────────────────────────────────────────────────────────────────────────────
//

/**
 * Returns the user’s "firstSteps" challenge or undefined if not found.
 */
const findFirstStepsChallenge = (user: any) => {
  return user?.challenges?.find(
    (challenge: any) => challenge.type === 'firstSteps'
  );
};

/**
 * Returns the tasks array from the "firstSteps" challenge; otherwise returns an empty array.
 */
const findFirstStepsTasks = (user: any) => {
  const firstStepsChallenge = findFirstStepsChallenge(injectCustomSteps(user));
  return firstStepsChallenge?.tasks || [];
};

/**
 * Finds the status of the firstSteps task matching the given screen path.
 */
const getTaskStatusByScreen = (user: any, screenPath: string) => {
  const tasks = findFirstStepsTasks(user);
  return tasks?.find((task: any) => task?.screen === screenPath)?.status;
};

/**
 * Formats the end date of a license as MM/YYYY.
 */
const formatLicenseEndDate = (licenceEndDate: any): string => {
  const date = new Date(licenceEndDate);
  return `${date.getUTCMonth() < 9 ? '0' : ''}${
    date.getUTCMonth() + 1
  }/${date.getFullYear()}`;
};

//
// ──────────────────────────────────────────────────────────────────────────────
//   > Challenge-Specific Functions
// ──────────────────────────────────────────────────────────────────────────────
//

export const getFirstStepsChallenge = (
  challenges: Array<any>
): any | null | undefined =>
  challenges.find((challenge) => challenge.type === 'firstSteps');

export const isFirstStepsScreen = () => {
  return document.location.href.includes('/onboarding');
};

export const isIncompleteProfile = (user: any) => {
  const hasPayment =
    user?.paymentMethods?.length &&
    user?.paymentMethods.find((payment: any) => payment.status === 'verified');
  const isVerifiedPayment = isPaymentRequired(user) ? hasPayment : true;
  const isVerifiedPhone = isPhoneVerificationRequired(user)
    ? user.verified.phoneNumber
    : true;
  const isVerifiedAddress = isAddressVerificationRequired(user)
    ? user.verified.address
    : true;
  const isVerifiedIdDocument = isIDVerificationRequired(user)
    ? getIDVerificationStatus(user) === 'completed' &&
      user?.verified?.identityDocument
    : true;
  const isVerifiedDLDocument = isDLVerificationRequired(user)
    ? getDLVerificationStatus(user) === 'completed' &&
      user?.verified?.drivingLicense
    : true;
  const isVerifiedEmail = isEmailVerificationRequired(user)
    ? getEmailVerificationStatus(user) === 'completed'
    : true;
  const tariffSelected = isTariffEnabled(user)
    ? getTariffStatus(user) === 'completed'
    : true;
  const depositPaied = isDepositRequired(user)
    ? getDepositStatus(user) === 'completed'
    : true;
  const pinSet = isPinRequired(user)
    ? getPinStatus(user) === 'completed'
    : true;

  // Updated custom steps completion check
  const areCustomStepsCompleted = Object.values(CUSTOM_STEPS).every((step) => {
    if (!step.shouldShow(user)) return true;

    // Special handling for SCHUFA: only require completion if user has SEPA payment
    if (step.screen === '/user/schufa') {
      const hasSepaPayment = user?.paymentMethods?.some(
        (payment: any) => payment?.methodType === 'sepa'
      );
      return hasSepaPayment ? step.isCompleted(user) : true;
    }

    return step.isCompleted(user);
  });

  return !(
    user &&
    isVerifiedEmail &&
    isVerifiedPhone &&
    tariffSelected &&
    isVerifiedAddress &&
    isVerifiedIdDocument &&
    isVerifiedDLDocument &&
    isVerifiedPayment &&
    depositPaied &&
    areCustomStepsCompleted &&
    pinSet
  );
};

export const getPhoneStatus = (user: any) => {
  const phoneStatus = getTaskStatusByScreen(user, '/user/verify-phone-number');
  return phoneStatus !== 'completed'
    ? phoneStatus
    : user?.verified.phoneNumber
    ? 'completed'
    : 'open';
};

export const isAddressFull = (address: any): boolean =>
  !!address &&
  !!address.streetName &&
  !!address.streetName.length &&
  !!address.streetNumber &&
  !!address.streetNumber.length &&
  !!address.city &&
  !!address.city.length &&
  !!address.postalCode &&
  !!address.postalCode.length;

export const getAddressStatus = (user: any) => {
  const addressTaskStatus = getTaskStatusByScreen(user, '/user/enter-address');
  return isAddressFull(user?.address) ? addressTaskStatus : 'open';
};

export const getLicenseStatus = (user: any) => {
  const licenseStatus = getTaskStatusByScreen(
    user,
    '/user/verify-drivers-license'
  );
  return licenseStatus !== 'completed'
    ? licenseStatus
    : user?.verified.drivingLicense
    ? 'completed'
    : 'deleted';
};

export const getIDStatus = (user: any) => {
  const idStatus = getTaskStatusByScreen(user, '/user/verify-id-document');
  return idStatus !== 'completed'
    ? idStatus
    : user?.verified.identityDocument
    ? 'completed'
    : 'deleted';
};

export const formatLicenseStatus = (
  license: any,
  licenseStatus: string,
  localize: any
): string => {
  if (licenseStatus === 'completed') {
    if (license?.interval?.to) {
      return `${localize(
        'driver.license.status.verified'
      )} ${formatLicenseEndDate(license.interval.to)}`;
    }
    return localize('driver.license.status.verified');
  }
  if (licenseStatus === 'inProgress') {
    return localize('driver.license.status.verifying');
  }
  if (licenseStatus === 'deleted') {
    return localize('driver.license.status.deleted');
  }
  return localize('driver.license.status.notuploaded');
};

export const formatIDStatus = (idState: any, localize: any): string => {
  if (idState === 'completed') {
    return localize('driver.id.status.verified');
  }

  if (idState === 'inProgress') {
    return localize('driver.id.status.verifying');
  }

  // @ts-ignore
  if (idState === 'deleted') {
    return localize('driver.id.status.open');
  }

  return localize('driver.id.status.open');
};

export const getEmailVerificationStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/verify-email');
};

export const getPhoneVerificationStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/verify-phone-number');
};

export const getAddressVerificationStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/enter-address');
};

export const getPaymentStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/payment-methods');
};

export const getIDVerificationStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/verify-id-document');
};

export const getDLVerificationStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/verify-drivers-license');
};

export const getDepositStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/pay-deposit');
};

export const getTariffStatus = (user: any) => {
  // This one still needs to handle fallback for tasks that might have text containing 'selectTariff'
  const tasks = findFirstStepsTasks(user);
  return tasks.find(
    (task: any) =>
      task?.screen === '/user/select-tariff' ||
      task?.text?.includes('selectTariff')
  )?.status;
};

export const getPinStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/enter-pin');
};

export const isEmailVerificationRequired = (user: any) => {
  return getEmailVerificationStatus(user) != undefined;
};

export const isPhoneVerificationRequired = (user: any) => {
  return getPhoneVerificationStatus(user) != undefined;
};

export const isAddressVerificationRequired = (user: any) => {
  return getAddressVerificationStatus(user) != undefined;
};

export const isTariffRequired = (user: any) => {
  return getTariffStatus(user) != undefined;
};

export const isPaymentRequired = (user: any) => {
  return getPaymentStatus(user) != undefined;
};

export const isDLVerificationRequired = (user: any) => {
  return getDLVerificationStatus(user) != undefined;
};

export const isIDVerificationRequired = (user: any) => {
  return getIDVerificationStatus(user) != undefined;
};

export const isDepositRequired = (user: any) => {
  return getDepositStatus(user) != undefined;
};

export const isTariffEnabled = (user: any) => {
  return getTariffStatus(user) != undefined;
};

export const isTariffOpen = (user: any) => {
  return getTariffStatus(user) === 'open';
};

export const isPinRequired = (user: any) => {
  return getPinStatus(user) != undefined;
};

export const getDobStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/dob');
};

export const isDobRequired = (user: any) => {
  return getDobStatus(user) != undefined;
};

export const getSchufaStatus = (user: any) => {
  return getTaskStatusByScreen(user, '/user/schufa');
};

export const isShufaRequired = (user: any) => {
  return getSchufaStatus(user) != undefined;
};

/**
 * Returns the total number of challenges, after deducting non-onboarding tasks.
 * For KYC apps (when !isMVV()), it deducts both signup and address challenges (Payment + Address in one screen);
 * for Rental apps, it deducts the signup challenge.
 *
 * Deduction Logic:
 * ┌────────────────────────────────────────┬───────────┬─────────────────────────────────────┐
 * │ Condition                              │ Deduction │ Reason                              │
 * ├────────────────────────────────────────┼───────────┼─────────────────────────────────────┤
 * │ KYC app & !separateCreditCheck         │ 2         │ Deduct signup + address (combined)  │
 * │   & !isMVV() & !isFRM()                │           │                                     │
 * │ All other cases                        │ 1         │ Deduct signup only                  │
 * └────────────────────────────────────────┴───────────┴─────────────────────────────────────┘
 */
export const getNumberOfChallenges = (
  user: any,
  separateCreditCheckScreen = false
) => {
  const tasks = findFirstStepsTasks(user);
  const total = tasks?.length || 0;
  return Math.max(
    0,
    isKYCApp() && !separateCreditCheckScreen && !isMVV() && !isFRM()
      ? total - 2
      : total - 1
  );
};

/**
 * Returns the total number of challenges completed based on rigorous checks.
 *
 * For each challenge in the onboarding order, we check:
 *  - Email: if required, its task must be 'completed'
 *  - Phone: if required, user.verified.phoneNumber must be present
 *  - Tariff: if enabled, its task must be 'completed'
 *  - ID: if required, status must be 'inProgress' OR ('completed' and user.verified.identityDocument exists)
 *  - Driving License: if required, status must be 'inProgress' OR ('completed' and user.verified.drivingLicense exists)
 *  - Payment: if required, its task must be 'completed' and a verified payment exists
 *  - Address: if required, user.verified.address must be present
 *  - Deposit: task is considered completed if its status is 'completed'
 *
 * Finally, we subtract a deduction:
 *  For KYC apps (when !isMVV()), it deducts both payment and address challenges (Payment + Address in one screen);
 *  For Rental apps, deduct the signup challenge.
 *
 * Scenarios (when task is counted as completed):
 * ┌─────────────────────────┬─────────────────────────┬───────────────┬────────────┬─────────────────────────────────────┐
 * │ Task                    │ Status                  │ Verified Flag │ Completed? │ Reason                              │
 * ├─────────────────────────┼─────────────────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Email                   │ completed               │ -             │ Yes        │ Email verified                      │
 * │ Email                   │ !completed              │ -             │ No         │ Not verified                        │
 * ├─────────────────────────┼─────────────────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Phone                   │ -                       │ true          │ Yes        │ Phone verified                      │
 * │ Phone                   │ -                       │ false         │ No         │ Phone not verified                  │
 * ├─────────────────────────┼─────────────────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Tariff                  │ completed               │ -             │ Yes        │ Tariff selected                     │
 * │ Tariff                  │ !completed              │ -             │ No         │ Tariff not selected                 │
 * ├─────────────────────────┼─────────────────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ ID / DL                 │ inProgress              │ -             │ Yes        │ User uploaded, waiting for review   │
 * │ ID / DL                 │ completed               │ true          │ Yes        │ Document verified                   │
 * │ ID / DL                 │ completed               │ false         │ No         │ Document verification removed       │
 * │ ID / DL                 │ open                    │ -             │ No         │ Not submitted or rejected           │
 * ├─────────────────────────┼─────────────────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Payment                 │ completed               │ hasVerified   │ Yes        │ Has verified payment                │
 * │ Payment                 │ completed               │ !hasVerified  │ No         │ Payment deleted                     │
 * │ Payment                 │ !completed              │ -             │ No         │ Not completed                       │
 * ├─────────────────────────┼─────────────────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Address                 │ -                       │ true          │ Yes        │ Address verified                    │
 * │ Address                 │ -                       │ false         │ No         │ Address not verified                │
 * ├─────────────────────────┼─────────────────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Deposit                 │ completed               │ -             │ Yes        │ Deposit paid                        │
 * │ Deposit                 │ !completed              │ -             │ No         │ Deposit not paid                    │
 * └─────────────────────────┴─────────────────────────┴───────────────┴────────────┴─────────────────────────────────────┘
 */
export const getNumberOfCompletedChallenges = (user: any) => {
  const tasks = findFirstStepsTasks(user);
  let completedCount = 0;

  for (const task of tasks) {
    let completed = false;
    switch (task.screen) {
      case '/user/verify-email':
        completed = isEmailVerificationRequired(user)
          ? getEmailVerificationStatus(user) === 'completed'
          : true;
        break;
      case '/user/verify-phone-number':
        completed = isPhoneVerificationRequired(user)
          ? !!user.verified?.phoneNumber
          : true;
        break;
      case '/user/select-tariff':
        completed = isTariffEnabled(user)
          ? getTariffStatus(user) === 'completed'
          : true;
        break;
      case '/user/verify-id-document': {
        // inProgress = user uploaded and waiting for review, no action needed
        const idStatus = getIDVerificationStatus(user);
        completed = isIDVerificationRequired(user)
          ? idStatus === 'inProgress' ||
            (idStatus === 'completed' && !!user.verified?.identityDocument)
          : true;
        break;
      }
      case '/user/verify-drivers-license': {
        // inProgress = user uploaded and waiting for review, no action needed
        const dlStatus = getDLVerificationStatus(user);
        completed = isDLVerificationRequired(user)
          ? dlStatus === 'inProgress' ||
            (dlStatus === 'completed' && !!user.verified?.drivingLicense)
          : true;
        break;
      }
      case '/user/payment-methods': {
        const hasActivePayment = hasActivePaymentMethod(user);

        completed = isPaymentRequired(user)
          ? getPaymentStatus(user) === 'completed' && hasActivePayment
          : true;
        break;
      }
      case '/user/enter-address':
        completed = isAddressVerificationRequired(user)
          ? !!user.verified?.address
          : true;
        break;
      case '/user/pay-deposit':
        completed = task.status === 'completed';
        break;
      case '/user/dob':
        completed = CUSTOM_STEPS.DOB.isCompleted(user);
        break;
      case '/user/schufa':
        completed = CUSTOM_STEPS.SCHUFA.isCompleted(user);
        break;
      default:
        // For any other custom steps, check if it's a custom step first
        if (task.isCustomStep) {
          // Find the matching custom step definition and use its isCompleted check
          const customStep = Object.values(CUSTOM_STEPS).find(
            (step) => step.screen === task.screen
          );
          completed = customStep
            ? customStep.isCompleted(user)
            : task.status === 'completed';
        } else {
          // Standard behavior for regular steps
          completed = task.status === 'completed';
        }
        break;
    }
    if (completed) {
      completedCount++;
    }
  }

  // Deduct signup task for all apps (it's always completed and excluded from total)
  // For KYC apps: also deduct address if payment is completed (combined screen)
  const deduction = isKYCApp()
    ? !isMVV() && getPaymentStatus(user) === 'completed'
      ? 2 // deduct signup + address (combined with payment screen)
      : 1 // deduct signup
    : 1; // deduct signup for non-KYC (car sharing) apps
  return completedCount - deduction;
};

/**
 * Returns the first user task (from the firstSteps challenge) that is still incomplete
 * based on the relevant onboarding order. If `currentRouteSuffix` is provided, we skip
 * tasks up to (and including) the challenge that maps to it.
 *
 * @param user - The user object
 * @param currentRouteSuffix - (Optional) e.g. 'DLOnboarding'
 * @returns The first incomplete task, or `null` if none found
 *
 * Scenarios (when task is considered incomplete and returned):
 * ┌─────────────────────────┬─────────────┬───────────────┬─────────────┬─────────────────────────────────────┐
 * │ Task                    │ Status      │ Verified Flag │ Incomplete? │ Reason                              │
 * ├─────────────────────────┼─────────────┼───────────────┼─────────────┼─────────────────────────────────────┤
 * │ Any task                │ open        │ -             │ Yes         │ Not started                         │
 * │ Any task                │ inProgress  │ -             │ Yes         │ In progress (except ID/DL*)         │
 * ├─────────────────────────┼─────────────┼───────────────┼─────────────┼─────────────────────────────────────┤
 * │ Payment                 │ completed   │ !hasVerified  │ Yes         │ Payment was deleted                 │
 * │ Payment                 │ completed   │ hasVerified   │ No          │ Has verified payment                │
 * ├─────────────────────────┼─────────────┼───────────────┼─────────────┼─────────────────────────────────────┤
 * │ Phone                   │ completed   │ false         │ Yes         │ Phone verification removed          │
 * │ Phone                   │ completed   │ true          │ No          │ Phone verified                      │
 * ├─────────────────────────┼─────────────┼───────────────┼─────────────┼─────────────────────────────────────┤
 * │ ID / DL                 │ completed   │ false         │ Yes         │ Document verification removed       │
 * │ ID / DL                 │ completed   │ true          │ No          │ Document verified                   │
 * ├─────────────────────────┼─────────────┼───────────────┼─────────────┼─────────────────────────────────────┤
 * │ Address                 │ completed   │ false         │ Yes         │ Address verification removed        │
 * │ Address                 │ completed   │ true          │ No          │ Address verified                    │
 * └─────────────────────────┴─────────────┴───────────────┴─────────────┴─────────────────────────────────────┘
 *
 * *Note: ID/DL with status 'inProgress' is NOT considered incomplete here because user
 * has already uploaded and is waiting for review - no action needed from user.
 */
export const getNextUserOpenChallenge = (
  user: any,
  currentRouteSuffix?: string
) => {
  // 1) Determine the proper onboarding order
  const isTruelayerEnabled =
    store?.getState()?.appData?.common?.brandSettings?.trueLayerSettings
      ?.bankLoginEnabled;

  const separateCreditCheckScreen =
    store?.getState()?.appData?.common?.brandSettings?.kycSettings
      ?.separateCreditCheckScreen;

  const isDocumentsAfterDeposit =
    user?.deposit?.toPayForSepaUnlock >
    store?.getState()?.appData?.common?.brandSettings?.kycSettings
      ?.documentsAfterDepositMinAmount;

  const order = isKYCApp()
    ? separateCreditCheckScreen
      ? isDocumentsAfterDeposit
        ? KYC_ONBOARDING_ORDER_WITH_SCHUFA_SPLITTED_HIGH_DEPOSIT
        : KYC_ONBOARDING_ORDER_WITH_SCHUFA_SPLITTED
      : isTruelayerEnabled
      ? isDocumentsAfterDeposit
        ? KYC_ONBOARDING_ORDER_WITH_TRUELAYER_HIGH_DEPOSIT
        : KYC_ONBOARDING_ORDER_WITH_TRUELAYER
      : KYC_ONBOARDING_ORDER
    : CAR_SHARING_ONBOARDING_ORDER;

  // 2) Grab the user’s onboarding tasks
  const tasks = findFirstStepsTasks(user);

  // 3) If we have a currentRouteSuffix, figure out the corresponding path and skip to the next
  let startIndex = 0;
  if (currentRouteSuffix) {
    // e.g. currentRouteSuffix = 'DLOnboarding'
    const screensNames = isWeb()
      ? SCREEN_TO_ROUTE_SUFFIX
      : SCREEN_TO_STACK_NAME;

    const currentScreenPath = Object.keys(screensNames).find(
      (path) => screensNames[path] === currentRouteSuffix
    );
    if (currentScreenPath) {
      const currentIndex = order.indexOf(currentScreenPath);
      if (currentIndex !== -1) {
        startIndex = currentIndex + 1;
      }
    }
  }

  // 4) Return the first incomplete task from `startIndex` onward
  // We check actual completion state, not just task.status, because status might be stale
  // (e.g., payment deleted but status still 'completed')
  for (let i = startIndex; i < order.length; i++) {
    const screenPath = order[i];
    const task = tasks.find((t: any) => t.screen === screenPath);
    if (!task) continue;

    // For ID/DL: 'inProgress' means user uploaded and is waiting for review - skip these
    const isIdOrDl =
      screenPath === '/user/verify-id-document' ||
      screenPath === '/user/verify-drivers-license';

    if (isIdOrDl && task.status === 'inProgress') {
      continue; // No action needed from user, skip to next task
    }

    // Check if task is actually incomplete (not just status === 'open')
    let isActuallyIncomplete = task.status === 'open';

    // For certain tasks, also verify the actual state matches the status
    if (!isActuallyIncomplete && task.status === 'completed') {
      switch (screenPath) {
        case '/user/payment-methods': {
          // Payment task might show completed but payment was deleted or payment is pending
          const hasActivePayment = hasActivePaymentMethod(user);
          if (!hasActivePayment) {
            isActuallyIncomplete = true;
          }
          break;
        }
        case '/user/verify-phone-number':
          if (!user?.verified?.phoneNumber) {
            isActuallyIncomplete = true;
          }
          break;
        case '/user/verify-drivers-license':
          if (!user?.verified?.drivingLicense) {
            isActuallyIncomplete = true;
          }
          break;
        case '/user/verify-id-document':
          if (!user?.verified?.identityDocument) {
            isActuallyIncomplete = true;
          }
          break;
        case '/user/enter-address':
          if (!user?.verified?.address) {
            isActuallyIncomplete = true;
          }
          break;
      }
    }

    if (isActuallyIncomplete) {
      return task;
    }
  }

  // If none found, return null
  return null;
};

//
// ──────────────────────────────────────────────────────────────────────────────
//   > NEW: getNextUserOpenChallengeRoute
// ──────────────────────────────────────────────────────────────────────────────
//

/**
 * Returns the next open/in-progress challenge’s route, prefixed
 * with either "/dashboard" or "/onboarding" depending on isDashboardScreen().
 *
 * Example:
 *   - If next open challenge = '/user/verify-phone-number'
 *       => routeSuffix = 'addPhone'
 *       => final route = '/dashboard/addPhone'  (if isDashboardScreen === true)
 *       => or '/onboarding/addPhone'            (if isDashboardScreen === false)
 *
 * If `currentRouteSuffix` is provided, we only look for tasks _after_ the one matching
 * that suffix in the onboarding order array.
 *
 * Example:
 *   - If `currentRouteSuffix` = 'DLOnboarding' and the user is on KYC,
 *       we skip all tasks up to (and including) '/user/verify-drivers-license'
 *       and look for the next open task in the array, e.g. '/user/payment-methods' or '/user/pay-deposit'

 */
export const getNextUserOpenChallengeRoute = (
  user: any,
  currentRouteSuffix?: string,
  stack: string = ''
): string | null => {
  const isTruelayerEnabled =
    store?.getState()?.appData?.common?.brandSettings?.trueLayerSettings
      ?.bankLoginEnabled;

  // 1) Get the next open challenge, potentially skipping up to `currentRouteSuffix`
  const nextChallenge = getNextUserOpenChallenge(user, currentRouteSuffix);

  // 2) If no open challenge is found, default to /settings
  if (!nextChallenge) {
    return isWeb() ? '/settings' : 'Settings';
  }
  console.log('nextChallenge', nextChallenge);

  const routeSuffix = isWeb()
    ? SCREEN_TO_ROUTE_SUFFIX[nextChallenge.screen]
    : SCREEN_TO_STACK_NAME[nextChallenge.screen];
  if (!routeSuffix) {
    return null;
  }

  const prefix = isDashboardScreen() ? 'dashboard' : 'onboarding';

  // ─────────────────────────────────────────────────────────────────────────────
  //   Special handling for particular tasks
  // ─────────────────────────────────────────────────────────────────────────────

  // a) Payment + TrueLayer
  if (
    nextChallenge.screen === '/user/payment-methods' &&
    nextChallenge.status === 'open' &&
    isTruelayerEnabled &&
    !isV2BankFlow()
  ) {
    return `/${prefix}/selectBank`;
  }

  // b) Pay Deposit
  if (nextChallenge.screen === '/user/pay-deposit') {
    if (user?.deposit?.toPayForSepaUnlock && !user?.blocked) {
      return `/${prefix}/deposit`;
    }
  }

  // c) Skipping tariff for ctf
  // if (isCTF() && nextChallenge.screen === '/user/select-tariff') {
  //   return `/${prefix}/upsellings`;
  // }

  // If we’re on the dashboard and not in the 'onboarding' stack, go to /settings
  if (isDashboardScreen() && stack !== 'onboarding') {
    return '/settings';
  }

  console.log('getNextUserOpenChallengeRoute =>', `/${prefix}/${routeSuffix}`);

  return isWeb() ? `/onboarding/${routeSuffix}` : routeSuffix;
};

/**
 * Groups tasks into categories for the stepper wizard display.
 * Returns only non-empty groups (if a group has no required tasks, it's excluded).
 *
 * Group Mapping:
 * ┌────────────────────┬─────────────────────────────────────────────┬─────────────────────────────────────┐
 * │ Group              │ Screens                                     │ Condition                           │
 * ├────────────────────┼─────────────────────────────────────────────┼─────────────────────────────────────┤
 * │ createAccount      │ /user/register                              │ Always included                     │
 * │ contactData        │ /user/verify-email                          │ If email required                   │
 * │                    │ /user/verify-phone-number                   │ If phone required                   │
 * │ contractualData    │ /user/select-tariff                         │ If tariff enabled                   │
 * │ documentsData      │ /user/verify-id-document                    │ If ID required                      │
 * │                    │ /user/verify-drivers-license                │ If DL required                      │
 * │                    │ /user/enter-address                         │ If address required & !isKYCApp     │
 * │                    │ /user/dob                                   │ Always (custom step)                │
 * │                    │ /user/schufa                                │ Always (custom step)                │
 * │ paymentData        │ /user/payment-methods                       │ If payment required                 │
 * │ deposit            │ /user/pay-deposit                           │ Always included                     │
 * │ pin                │ /user/enter-pin                             │ Always included                     │
 * └────────────────────┴─────────────────────────────────────────────┴─────────────────────────────────────┘
 *
 * @returns Array of task groups, each containing { screen, status } objects
 */
export const getChallengesForStepper = (user: any) => {
  const tasks = findFirstStepsTasks(user);

  const tasksGroups = {
    createAccount: [],
    contactData: [],
    contractualData: [],
    documentsData: [],
    paymentData: [],
    deposit: [],
    pin: [],
  };

  for (const task of tasks) {
    switch (task.screen) {
      case '/user/register':
        tasksGroups.createAccount.push({
          screen: task.screen,
          status: task.status,
        });
        break;
      case '/user/verify-email':
        isEmailVerificationRequired(user)
          ? tasksGroups.contactData.push({
              screen: task.screen,
              status: task.status,
            })
          : false;
        break;
      case '/user/verify-phone-number':
        isPhoneVerificationRequired(user)
          ? tasksGroups.contactData.push({
              screen: task.screen,
              status: task.status,
            })
          : false;
        break;
      case '/user/select-tariff':
        isTariffEnabled(user)
          ? tasksGroups.contractualData.push({
              screen: task.screen,
              status: task.status,
            })
          : false;
        break;
      case '/user/verify-id-document':
        isIDVerificationRequired(user)
          ? tasksGroups.documentsData.push({
              screen: task.screen,
              status: task.status,
            })
          : false;
        break;
      case '/user/verify-drivers-license':
        isDLVerificationRequired(user)
          ? tasksGroups.documentsData.push({
              screen: task.screen,
              status: task.status,
            })
          : false;
        break;
      case '/user/payment-methods': {
        isPaymentRequired(user)
          ? tasksGroups.paymentData.push({
              screen: task.screen,
              status: task.status,
            })
          : false;
        break;
      }
      case '/user/enter-address':
        isAddressVerificationRequired(user) && !isKYCApp()
          ? tasksGroups.documentsData.push({
              screen: task.screen,
              status: task.status,
            })
          : false;
        break;
      case '/user/pay-deposit':
        tasksGroups.deposit.push({
          screen: task.screen,
          status: task.status,
        });
        break;
      case '/user/dob':
        tasksGroups.documentsData.push({
          screen: task.screen,
          status: task.status,
        });
        break;
      case '/user/schufa':
        tasksGroups.documentsData.push({
          screen: task.screen,
          status: task.status,
        });
        break;
      case '/user/enter-pin':
        tasksGroups.pin.push({
          screen: task.screen,
          status: task.status,
        });
        break;
      default:
        break;
    }
  }

  return Object.values(tasksGroups)?.filter((group) => group.length > 0);
};

export const getNumberOfValidGroups = (user: any) =>
  getChallengesForStepper(user)?.length;

/**
 * Check if a task is actually completed, not just by status but by actual verification state.
 * This handles cases where status is 'completed' but the actual data was removed (e.g., payment deleted).
 * For ID and DL: 'inProgress' is also considered complete since user has uploaded and is waiting for review.
 *
 * Scenarios:
 * ┌─────────────────────────┬─────────────┬───────────────┬────────────┬─────────────────────────────────────┐
 * │ Task                    │ Status      │ Verified Flag │ Returns    │ Reason                              │
 * ├─────────────────────────┼─────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ ID / DL                 │ inProgress  │ -             │ true       │ User uploaded, waiting for review   │
 * │ ID / DL                 │ completed   │ true          │ true       │ Fully verified                      │
 * │ ID / DL                 │ completed   │ false         │ false      │ Verification data removed           │
 * │ ID / DL                 │ open        │ -             │ false      │ Rejected or not started             │
 * ├─────────────────────────┼─────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Payment                 │ completed   │ hasVerified   │ true       │ Has verified payment method         │
 * │ Payment                 │ completed   │ !hasVerified  │ false      │ Payment deleted                     │
 * │ Payment                 │ inProgress  │  has pending  │ true       │ Payment pending                     │
 * │ Payment                 │ open        │ -             │ false      │ Not started                         │
 * ├─────────────────────────┼─────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Phone                   │ completed   │ true          │ true       │ Phone verified                      │
 * │ Phone                   │ completed   │ false         │ false      │ Phone verification removed          │
 * ├─────────────────────────┼─────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Address                 │ completed   │ true          │ true       │ Address verified                    │
 * │ Address                 │ completed   │ false         │ false      │ Address verification removed        │
 * ├─────────────────────────┼─────────────┼───────────────┼────────────┼─────────────────────────────────────┤
 * │ Other tasks             │ completed   │ -             │ true       │ Trust task status                   │
 * │ Other tasks             │ !completed  │ -             │ false      │ Not completed                       │
 * └─────────────────────────┴─────────────┴───────────────┴────────────┴─────────────────────────────────────┘
 */
const isTaskActuallyCompleted = (user: any, task: any): boolean => {
  // For ID and DL, 'inProgress' means user has uploaded and is waiting for verification
  // No action required from user, so consider it complete for wizard purposes
  if (task.screen === '/user/verify-id-document') {
    if (task.status === 'inProgress') {
      return true; // User is waiting for verification, no action needed
    }
    if (task.status === 'completed') {
      return !!user?.verified?.identityDocument;
    }
    return false;
  }

  if (task.screen === '/user/verify-drivers-license') {
    if (task.status === 'inProgress') {
      return true; // User is waiting for verification, no action needed
    }
    if (task.status === 'completed') {
      return !!user?.verified?.drivingLicense;
    }
    return false;
  }

  if (task.status !== 'completed') {
    return false;
  }

  // For certain tasks, verify the actual state matches the status
  switch (task.screen) {
    case '/user/payment-methods': {
      return hasActivePaymentMethod(user);
    }
    case '/user/verify-phone-number':
      return !!user?.verified?.phoneNumber;
    case '/user/enter-address':
      return !!user?.verified?.address;
    default:
      return true;
  }
};

/**
 * Returns the number of completed groups for the stepper wizard (e.g., "3/4").
 * A group is completed only if ALL tasks within that group are actually completed.
 * Uses isTaskActuallyCompleted() to verify actual state, not just task status.
 *
 * Groups (from getChallengesForStepper):
 * ┌────────────────────┬────────────────────────────────────────────────────────────┐
 * │ Group              │ Tasks                                                      │
 * ├────────────────────┼────────────────────────────────────────────────────────────┤
 * │ createAccount      │ /user/register                                             │
 * │ contactData        │ /user/verify-email, /user/verify-phone-number              │
 * │ contractualData    │ /user/select-tariff                                        │
 * │ documentsData      │ /user/verify-id-document, /user/verify-drivers-license,    │
 * │                    │ /user/enter-address (non-KYC), /user/dob, /user/schufa     │
 * │ paymentData        │ /user/payment-methods                                      │
 * │ deposit            │ /user/pay-deposit                                          │
 * │ pin                │ /user/enter-pin                                            │
 * └────────────────────┴────────────────────────────────────────────────────────────┘
 *
 * Example: If documentsData has [ID: inProgress, DL: completed+verified], group is complete
 *          because inProgress is treated as complete (no user action needed).
 */
export const getNumberOfCompletedGroups = (user: any) =>
  getChallengesForStepper(user)?.filter((arr) =>
    arr?.every((element) => isTaskActuallyCompleted(user, element))
  ).length;

//
// ──────────────────────────────────────────────────────────────────────────────
//   > MOBILE: Navigate to mobile screens from either the Dashboard or Onboarding flows.
// ──────────────────────────────────────────────────────────────────────────────
//

const getStackName = (navigation) => {
  const parent = navigation.getParent();
  const parentState = parent?.getState();

  const activeParentRoute = parentState?.routes[parentState.index];
  const sourceStack = activeParentRoute?.name;

  return sourceStack;
};

export const isDashboard = (navigation) => {
  const sourceStack = getStackName(navigation);

  if (sourceStack === 'DashboardStack') {
    return true;
  }
};

export const isBookingViolation = (navigation) => {
  const sourceStack = getStackName(navigation);

  if (sourceStack === 'BookingViolationsStack') {
    return true;
  }
};

export const isOnboarding = (navigation) => {
  const sourceStack = getStackName(navigation);

  if (sourceStack === 'FirstStepsStack') {
    return true;
  }
};
